/**
 * Edit Health Record Page - Edit existing health record entries
 * Mapped to backend HealthRecordDTO with exact field names
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, FileText, Search, ChevronDown, Heart } from 'lucide-react';
import {
  healthRecordsApi,
  cattleApi,
  type HealthRecord,
  type Cattle
} from '../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../utils/auth';

interface HealthRecordFormData {
  cattleId: number;
  recordType: string;
  recordDate: string;
  veterinarianName: string;
  veterinarianLicense: string;
  veterinarianContact: string;
  diagnosis: string;
  treatment: string;
  medications: string;
  dosageInstructions: string;
  vaccinationType: string;
  nextVaccinationDate: string;
  nextCheckupDate: string;
  cost: number;
  notes: string;
  performedBy: string;
  status: string;
}

// InputField Component - defined outside to prevent recreation
const InputField = memo(({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  error,
  maxLength,
  className = '',
  isPhoneNumber = false,
  ...props
}: any) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (isPhoneNumber) {
      // Only allow digits and limit to 10 characters
      newValue = newValue.replace(/[^0-9]/g, '').slice(0, 10);
    }
    if (type === 'number') {
      onChange(Number(newValue));
    } else {
      onChange(newValue);
    }
  }, [type, isPhoneNumber, onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
        {maxLength && (
          <span className="text-xs text-gray-500 font-normal ml-2">
            ({String(value || '').length}/{maxLength})
          </span>
        )}
      </label>
      <input
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
        {...props}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
InputField.displayName = 'InputField';

const SelectField = memo(({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  options,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((option: any) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
));
SelectField.displayName = 'SelectField';

const TextAreaField = memo(({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  rows = 3,
  maxLength,
  className = ''
}: any) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
        {maxLength && (
          <span className="text-xs text-gray-500 font-normal ml-2">
            ({String(value || '').length}/{maxLength})
          </span>
        )}
      </label>
      <textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
TextAreaField.displayName = 'TextAreaField';

// CostField Component - defined outside to prevent recreation
const CostField = memo(({
  label,
  value,
  onChange,
  error,
  className = ''
}: any) => {
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value) || 0);
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">
        {label}
      </label>

      <div className="relative flex items-center">
        <input
          type="number"
          value={value || 0}
          onChange={handleInputChange}
          placeholder="0"
          min="0"
          step="1"
          className={`w-full px-3 py-2 pl-8 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent hover:border-gray-400 ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
        />

        <span className="absolute left-3 text-lg font-semibold text-gray-600">₹</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
});
CostField.displayName = 'CostField';

// SearchableSelectField Component for cattle selection
const SearchableSelectField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  options,
  className = ''
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(false);

  // Get selected option name
  const selectedOption = options.find((opt: any) => opt.id === value);
  const displayValue = selectedOption ? selectedOption.name : placeholder;

  // Filter options based on search term
  const filteredOptions = options.filter((option: any) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Focus search input when dropdown opens (only once)
  useEffect(() => {
    if (isOpen && searchInputRef.current && !isOpenRef.current) {
      isOpenRef.current = true;
      searchInputRef.current?.focus();
    } else if (!isOpen) {
      isOpenRef.current = false;
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`space-y-2 ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {/* Selected Value Display */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-gray-500">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option: any) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onChange(option.id);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors ${
                      option.id === value ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'
                    }`}
                  >
                    {option.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
};

export default function EditHealthRecord() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Dropdown data from backend APIs
  const [cattle, setCattle] = useState<{ id: number; name: string }[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Field validation rules with min/max lengths
  const VALIDATION_RULES = {
    veterinarianName: { min: 2, max: 100 },
    veterinarianLicense: { min: 2, max: 50 },
    veterinarianContact: { min: 10, max: 10 },
    diagnosis: { min: 5, max: 500 },
    treatment: { min: 5, max: 500 },
    medications: { min: 5, max: 500 },
    dosageInstructions: { min: 5, max: 500 },
    vaccinationType: { min: 2, max: 50 },
    performedBy: { min: 2, max: 100 },
    notes: { min: 0, max: 1000 }
  };

  const [formData, setFormData] = useState<HealthRecordFormData>({
    cattleId: 0,
    recordType: '',
    recordDate: '',
    veterinarianName: '',
    veterinarianLicense: '',
    veterinarianContact: '',
    diagnosis: '',
    treatment: '',
    medications: '',
    dosageInstructions: '',
    vaccinationType: '',
    nextVaccinationDate: '',
    nextCheckupDate: '',
    cost: 0,
    notes: '',
    performedBy: '',
    status: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof HealthRecordFormData, string>>>({});

  useEffect(() => {
    // Fetch cattle from backend API
    const fetchDropdownData = async () => {
      setDataLoading(true);
      try {
        const cattleResponse = await cattleApi.getAllCattle(0, 1000);

        // Process cattle data
        if (cattleResponse.success && cattleResponse.data?.content) {
          const cattleList = cattleResponse.data.content.map((c: Cattle) => ({
            id: c.id!,
            name: `${c.uniqueAnimalId} - ${c.name}`
          }));
          setCattle(cattleList);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load cattle data. Please refresh the page.'
        });
      } finally {
        setDataLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const result = await healthRecordsApi.getHealthRecordById(parseInt(id));

        if (result.success && result.data) {
          const record = result.data;
          // Convert ISO date strings to YYYY-MM-DD format for date inputs
          const formatDateForInput = (dateString?: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            } catch {
              return '';
            }
          };

          setFormData({
            cattleId: record.cattleId || 0,
            recordType: record.recordType || '',
            recordDate: formatDateForInput(record.recordDate),
            veterinarianName: record.veterinarianName || '',
            veterinarianLicense: record.veterinarianLicense || '',
            veterinarianContact: record.veterinarianContact || '',
            diagnosis: record.diagnosis || '',
            treatment: record.treatment || '',
            medications: record.medications || '',
            dosageInstructions: record.dosageInstructions || '',
            vaccinationType: record.vaccinationType || '',
            nextVaccinationDate: formatDateForInput(record.nextVaccinationDate),
            nextCheckupDate: formatDateForInput(record.nextCheckupDate),
            cost: record.cost || 0,
            notes: record.notes || '',
            performedBy: record.performedBy || '',
            status: record.status || ''
          });
        } else {
          setMessage({
            type: 'error',
            text: result.error || 'Failed to load health record.'
          });
        }
      } catch (error) {
        console.error('Error fetching health record:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load health record.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id]);

  const handleInputChange = (field: keyof HealthRecordFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof HealthRecordFormData, string>> = {};

    if (formData.cattleId === 0) newErrors.cattleId = 'Cattle is required';
    if (!formData.recordDate.trim()) newErrors.recordDate = 'Record Date is required';
    if (!formData.recordType.trim()) newErrors.recordType = 'Record Type is required';

    // Next Checkup Date must be after Record Date
    if (formData.nextCheckupDate && formData.recordDate) {
      if (formData.nextCheckupDate <= formData.recordDate) {
        newErrors.nextCheckupDate = 'Next Checkup Date must be after the Record Date';
      }
    }

    // Next Vaccination Date must be after Record Date
    if (formData.nextVaccinationDate && formData.recordDate) {
      if (formData.nextVaccinationDate <= formData.recordDate) {
        newErrors.nextVaccinationDate = 'Next Vaccination Date must be after the Record Date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields.'
      });
      return;
    }

    if (!id) {
      setMessage({
        type: 'error',
        text: 'Invalid health record ID.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const healthRecordData: Omit<HealthRecord, 'id' | 'createdBy' | 'updatedBy' | 'isActive' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
        cattleId: formData.cattleId,
        recordType: formData.recordType,
        recordDate: formData.recordDate ? new Date(formData.recordDate).toISOString().split('T')[0] : '',
        veterinarianName: formData.veterinarianName || undefined,
        veterinarianLicense: formData.veterinarianLicense || undefined,
        veterinarianContact: formData.veterinarianContact || undefined,
        diagnosis: formData.diagnosis || undefined,
        treatment: formData.treatment || undefined,
        medications: formData.medications || undefined,
        dosageInstructions: formData.dosageInstructions || undefined,
        vaccinationType: formData.vaccinationType || undefined,
        nextVaccinationDate: formData.nextVaccinationDate ? new Date(formData.nextVaccinationDate).toISOString().split('T')[0] : undefined,
        nextCheckupDate: formData.nextCheckupDate ? new Date(formData.nextCheckupDate).toISOString().split('T')[0] : undefined,
        cost: formData.cost || undefined,
        notes: formData.notes || undefined,
        performedBy: formData.performedBy || undefined,
        status: formData.status || undefined
      };

      console.log('Updating health record:', healthRecordData);
      const result = await healthRecordsApi.updateHealthRecord(parseInt(id), healthRecordData);

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Health record updated successfully!'
        });

        // Navigate back after delay
        setTimeout(() => {
          navigate('/gaushala/health-history');
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to update health record.'
        });
      }
    } catch (error) {
      console.error('Error updating health record:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update health record. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/health-history');
  };

  if (loading || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading form data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Health Record</h1>
          <p className="text-gray-600 mt-1">Update health record #{id}</p>
        </div>
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <span className="text-green-500">✓</span>
            ) : (
              <span className="text-red-500">⚠</span>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
            <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
              <Heart className="h-5 w-5 text-red-600" />
            </div>
            Edit Health Record Information
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cattle - Searchable */}
            <SearchableSelectField
              label="Cattle"
              value={formData.cattleId}
              onChange={(value: number) => handleInputChange('cattleId', value)}
              placeholder="Search and select cattle"
              required
              error={errors.cattleId}
              options={cattle}
            />

            {/* Record Type */}
            <SelectField
              label="Record Type"
              value={formData.recordType}
              onChange={(value: string) => handleInputChange('recordType', value)}
              placeholder="Select record type"
              required
              error={errors.recordType}
              options={[
                { id: 'VACCINATION', name: 'Vaccination' },
                { id: 'TREATMENT', name: 'Treatment' },
                { id: 'CHECKUP', name: 'Checkup' },
                { id: 'SURGERY', name: 'Surgery' },
                { id: 'INJURY', name: 'Injury' }
              ]}
            />

            {/* Record Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Record Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.recordDate}
                  onChange={(e) => handleInputChange('recordDate', e.target.value)}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer ${
                    errors.recordDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.recordDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.recordDate}
                </div>
              )}
            </div>

            {/* Status */}
            <SelectField
              label="Status"
              value={formData.status}
              onChange={(value: string) => handleInputChange('status', value)}
              placeholder="Select status"
              error={errors.status}
              options={[
                { id: 'SCHEDULED', name: 'Scheduled' },
                { id: 'COMPLETED', name: 'Completed' },
                { id: 'CANCELLED', name: 'Cancelled' }
              ]}
            />

            {/* Veterinarian Name */}
            <InputField
              label="Veterinarian Name"
              value={formData.veterinarianName}
              onChange={(value: string) => handleInputChange('veterinarianName', value)}
              placeholder="Veterinarian name"
              error={errors.veterinarianName}
            />

            {/* Veterinarian License */}
            <InputField
              label="Veterinarian License"
              value={formData.veterinarianLicense}
              onChange={(value: string) => handleInputChange('veterinarianLicense', value)}
              placeholder="License number"
              error={errors.veterinarianLicense}
            />

            {/* Veterinarian Contact */}
            <InputField
              label="Veterinarian Contact"
              value={formData.veterinarianContact}
              onChange={(value: string) => handleInputChange('veterinarianContact', value)}
              placeholder="Enter 10-digit contact number"
              isPhoneNumber={true}
              maxLength={10}
              error={errors.veterinarianContact}
            />

            {/* Diagnosis */}
            <InputField
              label="Diagnosis"
              value={formData.diagnosis}
              onChange={(value: string) => handleInputChange('diagnosis', value)}
              placeholder="Diagnosis details"
              error={errors.diagnosis}
            />

            {/* Treatment */}
            <InputField
              label="Treatment"
              value={formData.treatment}
              onChange={(value: string) => handleInputChange('treatment', value)}
              placeholder="Treatment provided"
              error={errors.treatment}
            />

            {/* Medications */}
            <InputField
              label="Medications"
              value={formData.medications}
              onChange={(value: string) => handleInputChange('medications', value)}
              placeholder="Medication names"
              error={errors.medications}
            />

            {/* Dosage Instructions */}
            <InputField
              label="Dosage Instructions"
              value={formData.dosageInstructions}
              onChange={(value: string) => handleInputChange('dosageInstructions', value)}
              placeholder="e.g., 500mg twice daily"
              error={errors.dosageInstructions}
            />

            {/* Vaccination Type */}
            <InputField
              label="Vaccination Type"
              value={formData.vaccinationType}
              onChange={(value: string) => handleInputChange('vaccinationType', value)}
              placeholder="Type of vaccine"
              error={errors.vaccinationType}
            />

            {/* Next Vaccination Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">Next Vaccination Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.nextVaccinationDate}
                  onChange={(e) => handleInputChange('nextVaccinationDate', e.target.value)}
                  min={formData.recordDate ? (() => { const d = new Date(formData.recordDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })() : undefined}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer ${
                    errors.nextVaccinationDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.nextVaccinationDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.nextVaccinationDate}
                </div>
              )}
            </div>

            {/* Next Checkup Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">Next Checkup Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.nextCheckupDate}
                  onChange={(e) => handleInputChange('nextCheckupDate', e.target.value)}
                  min={formData.recordDate ? (() => { const d = new Date(formData.recordDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })() : undefined}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer ${
                    errors.nextCheckupDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.nextCheckupDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.nextCheckupDate}
                </div>
              )}
            </div>

            {/* Cost */}
            <CostField
              label="Cost (₹)"
              value={formData.cost}
              onChange={(value: number) => handleInputChange('cost', value)}
              error={errors.cost}
            />

            {/* Performed By */}
            <InputField
              label="Performed By"
              value={formData.performedBy}
              onChange={(value: string) => handleInputChange('performedBy', value)}
              placeholder="Person who performed the procedure"
              error={errors.performedBy}
            />

            {/* Notes */}
            <TextAreaField
              label="Notes"
              value={formData.notes}
              onChange={(value: string) => handleInputChange('notes', value)}
              placeholder="Additional notes or observations"
              className="md:col-span-2"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {isLoading ? 'Updating...' : 'Update Health Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
